<template>
  <div class="cart-freebie-card" :class="{ [$class('loading')]: !product }">
    <input
      :id="name"
      :checked="selected"
      class="cart-freebie-card__input"
      :disabled="disabled"
      :name="name"
      type="checkbox"
      @input.prevent.stop="handleInputChange"
    />

    <label class="cart-freebie-card__card" :for="name">
      <span
        v-if="product && !product.selectedOrFirstAvailableVariant?.available"
        class="cart-freebie-card__sold-out text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
        v-html="availableString"
      />

      <div class="cart-freebie-card__image-container">
        <responsive-image
          class="cart-freebie-card__image"
          :image="featuredImage"
          :ratio="$variable('product.imageRatio')"
          sizes="53px"
          widths="53, 106, 159"
        />
      </div>

      <div class="cart-freebie-card__details">
        <p
          class="cart-freebie-card__title text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
          v-text="product?.title"
        />

        <p
          v-if="!product.hasOnlyDefaultVariant"
          class="cart-freebie-card__subtitle text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
          v-text="product.selectedOrFirstAvailableVariant?.title"
        />

        <p
          class="cart-freebie-card__price text-mobile-paragraph-xs-bold text-mobile-paragraph-xs-bold"
          v-text="$string('product.price.free')"
        />
      </div>

      <span
        class="cart-freebie-card__checkbox"
        :class="{ [$class('hidden')]: !showCheckbox }"
      >
        <icon-check-outline class="cart-freebie-card__check" />
      </span>
    </label>
  </div>
</template>

<script>
/**
 * Vue: Cart freebie card (cart-freebies)
 * -----------------------------------------------------------------------------
 * Single variant tile for cart freebies.
 *
 * @param {String} name - Form input name.
 * @param {Boolean|Object} product - Product to display.
 * @param {Number} queueLimit - Queue limit.
 * @param {Number} queueSize - Queue size.
 * @param {Boolean} [selected] - Pre-select checkbox.
 * @param {Boolean} [showCheckbox] - Display checkbox.
 *
 * @emits queueVariant - Add variant ID to queue.
 *
 */
import ResponsiveImage from '~global/images/responsive-image';

import IconCheckOutline from '~icons/general/check-outline.svg';

export default {
  name: 'CartFreebieCard',

  components: {
    IconCheckOutline,
    ResponsiveImage,
  },

  props: {
    name: {
      type: String,
      default: '',
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    queueLimit: {
      type: Number,
      default: 1,
      required: true,
    },
    queueSize: {
      type: Number,
      default: 0,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'select-product': (payload) => typeof payload === 'number',
  },

  computed: {
    /**
     * Compute featured image.
     */
    featuredImage() {
      return this.product.selectedOrFirstAvailableVariant
        ? this.product.selectedOrFirstAvailableVariant.featuredImage
        : this.product.featuredImage;
    },

    /**

     * Compute available string.
     * @returns {String}
     */
    availableString() {
      if (this.product.selectedOrFirstAvailableVariant.available) {
        return '';
      }

      if (
        this.product.selectedOrFirstAvailableVariant.availableState ===
        'back-soon'
      ) {
        return this.$string('product.badges.back_soon_html');
      }

      return this.$string('product.badges.sold_out_html');
    },

    /**
     * Compute if the card should be disabled.
     * only disables when there are several items selectable
     * @returns {Boolean}
     */
    disabled() {
      return (
        !this.product ||
        !this.product.selectedOrFirstAvailableVariant.available ||
        (this.queueSize >= this.queueLimit &&
          !this.selected &&
          this.queueLimit !== 1)
      );
    },
  },

  methods: {
    /**
     * Handle input change.
     * when there is only one freebie allowed to ensure it only allows
     * to add one freebie at a time. This is why the emit for queue variant
     * on input change
     */
    handleInputChange(event) {
      if (
        !this.selected &&
        this.queueLimit > 1 &&
        this.queueLimit === this.queueSize
      ) {
        event.target.checked = false;
        return false;
      }
      this.$emit(
        'select-product',
        this.product.selectedOrFirstAvailableVariant.id,
        !this.selected,
      );

      return true;
    },
  },
};
</script>

<style lang="scss">
@import './cart-freebie-card';
</style>
